// Session bootstrap (T071). On mount, calls GET /api/me via the typed api
// client and stores the owner in a React Context. On 401 the api layer
// already hard-redirects to /auth/google and throws a "redirecting" sentinel,
// so we swallow that error and leave `owner=null` while the navigation
// happens. No zustand/redux — plain React.

import {
  type ReactNode,
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { ApiError, type Owner, getMe } from "./api";

export type { Owner };

export interface SessionContextValue {
  owner: Owner | null;
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const DEFAULT_VALUE: SessionContextValue = {
  owner: null,
  loading: true,
  error: null,
  refresh: async () => {},
  logout: async () => {},
};

const SessionContext = createContext<SessionContextValue>(DEFAULT_VALUE);

async function postLogout(): Promise<void> {
  await fetch("/api/logout", { method: "POST", credentials: "include" });
}

export interface SessionProviderProps {
  children: ReactNode;
}

export function SessionProvider({ children }: SessionProviderProps): ReactNode {
  const [owner, setOwner] = useState<Owner | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const me = await getMe();
      setOwner(me);
    } catch (err) {
      // 401 already hard-redirected via the api client; swallow the sentinel
      // so we don't flash an error banner during navigation.
      if (err instanceof ApiError && err.status === 401) {
        setOwner(null);
      } else {
        setError(err instanceof Error ? err : new Error(String(err)));
        setOwner(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await postLogout();
    setOwner(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(
    () => ({ owner, loading, error, refresh, logout }),
    [owner, loading, error, refresh, logout],
  );

  return createElement(SessionContext.Provider, { value }, children);
}

export function useSession(): SessionContextValue {
  return useContext(SessionContext);
}
